import React, { useState } from 'react';
import { X, LogIn, UserPlus } from 'lucide-react';
import { authService } from '../services/auth';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const result = await authService.login(username, password);
        if (result.success) {
          onLoginSuccess();
          onClose();
        } else {
          setError(result.error || '登录失败');
        }
      } else {
        const result = await authService.register(username, email, password);
        if (result.success) {
          onLoginSuccess();
          onClose();
        } else {
          setError(result.error || '注册失败');
        }
      }
    } catch (err: any) {
      setError(err.message || '操作失败');
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (username: string, password: string) => {
    setLoading(true);
    setError('');
    setUsername(username);
    setPassword(password);

    try {
      const result = await authService.login(username, password);
      if (result.success) {
        onLoginSuccess();
        onClose();
      } else {
        setError(result.error || '登录失败');
      }
    } catch (err: any) {
      setError(err.message || '登录失败');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">{isLogin ? '登录游戏' : '注册账号'}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                邮箱
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                required
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              用户名
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-bold mb-2">
              密码
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 disabled:bg-gray-400 flex items-center justify-center"
          >
            {loading ? (
              <span>处理中...</span>
            ) : (
              <>
                {isLogin ? <LogIn size={20} className="mr-2" /> : <UserPlus size={20} className="mr-2" />}
                {isLogin ? '登录' : '注册'}
              </>
            )}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => setIsLogin(!isLogin)}
            className="text-blue-500 hover:text-blue-700"
          >
            {isLogin ? '没有账号？点击注册' : '已有账号？点击登录'}
          </button>
        </div>

        {/* 快速登录按钮（仅本地开发环境显示，生产构建会被剔除） */}
        {process.env.NODE_ENV === 'development' && isLogin && (
          <div className="mt-4 pt-4 border-t">
            <p className="text-sm text-gray-600 mb-2">快速登录（测试账号）：</p>
            <div className="flex gap-2">
              <button
                onClick={() => quickLogin('test@test.com', '123456')}
                className="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-gray-200"
              >
                测试用户
              </button>
              <button
                onClick={() => quickLogin('admin@test.com', '123456')}
                className="text-sm bg-gray-100 px-3 py-1 rounded hover:bg-gray-200"
              >
                管理员
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
